import { createSlice } from "@reduxjs/toolkit";

// Theme options
export const THEME_OPTIONS = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
};

// Safe browser environment check
const isBrowser = typeof window !== "undefined";

const getSystemTheme = () => {
  if (!isBrowser) return THEME_OPTIONS.LIGHT;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? THEME_OPTIONS.DARK
    : THEME_OPTIONS.LIGHT;
};

const getInitialTheme = () => {
  if (!isBrowser) return THEME_OPTIONS.SYSTEM;

  try {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme && Object.values(THEME_OPTIONS).includes(savedTheme)) {
      return savedTheme;
    }
  } catch (error) {
    console.warn("Could not access localStorage:", error);
  }

  return THEME_OPTIONS.SYSTEM;
};

const getEffectiveTheme = (theme) => {
  return theme === THEME_OPTIONS.SYSTEM ? getSystemTheme() : theme;
};

// Function to apply theme to document
const applyThemeToDocument = (effectiveTheme) => {
  if (!isBrowser) return;

  const root = document.documentElement;
  root.setAttribute("data-theme", effectiveTheme);
  root.style.colorScheme = effectiveTheme;

  // Update meta theme-color for mobile browsers
  let metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (!metaThemeColor) {
    metaThemeColor = document.createElement("meta");
    metaThemeColor.name = "theme-color";
    document.head.appendChild(metaThemeColor);
  }
  metaThemeColor.setAttribute(
    "content",
    effectiveTheme === "dark" ? "#0a0a0a" : "#ffffff",
  );
};

// Common theme update logic
const updateThemeState = (state, newTheme) => {
  state.theme = newTheme;
  state.effectiveTheme = getEffectiveTheme(newTheme);

  if (isBrowser) {
    try {
      localStorage.setItem("theme", newTheme);
    } catch (error) {
      console.warn("Could not save theme to localStorage:", error);
    }
  }

  applyThemeToDocument(state.effectiveTheme);
};

const themeSlice = createSlice({
  name: "theme",
  initialState: {
    theme: THEME_OPTIONS.SYSTEM,
    effectiveTheme: THEME_OPTIONS.LIGHT,
    systemTheme: THEME_OPTIONS.LIGHT,
    _initialized: false, // Track initialization
  },
  reducers: {
    toggleTheme: (state) => {
      const themes = Object.values(THEME_OPTIONS);
      const currentIndex = themes.indexOf(state.theme);
      const nextIndex = (currentIndex + 1) % themes.length;
      updateThemeState(state, themes[nextIndex]);
    },

    setTheme: (state, action) => {
      if (Object.values(THEME_OPTIONS).includes(action.payload)) {
        updateThemeState(state, action.payload);
      }
    },

    setLightTheme: (state) => {
      updateThemeState(state, THEME_OPTIONS.LIGHT);
    },

    setDarkTheme: (state) => {
      updateThemeState(state, THEME_OPTIONS.DARK);
    },

    setSystemTheme: (state) => {
      const systemTheme = getSystemTheme();
      state.theme = THEME_OPTIONS.SYSTEM;
      state.effectiveTheme = systemTheme;
      state.systemTheme = systemTheme;

      if (isBrowser) {
        try {
          localStorage.setItem("theme", THEME_OPTIONS.SYSTEM);
        } catch (error) {
          console.warn("Could not save theme to localStorage:", error);
        }
      }

      applyThemeToDocument(state.effectiveTheme);
    },

    updateSystemTheme: (state) => {
      const newSystemTheme = getSystemTheme();
      state.systemTheme = newSystemTheme;

      if (state.theme === THEME_OPTIONS.SYSTEM) {
        state.effectiveTheme = newSystemTheme;
        applyThemeToDocument(state.effectiveTheme);
      }
    },

    // Initialize theme on app start
    initializeTheme: (state) => {
      if (state._initialized) return; // Prevent re-initialization

      const savedTheme = getInitialTheme();
      state.theme = savedTheme;
      state.effectiveTheme = getEffectiveTheme(savedTheme);
      state.systemTheme = getSystemTheme();
      state._initialized = true;

      applyThemeToDocument(state.effectiveTheme);
    },
  },
});

// Theme detection utility with proper store handling
export const detectSystemThemeChange = (store) => {
  if (!isBrowser) return () => {};

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  const handleChange = (e) => {
    store.dispatch(updateSystemTheme());
  };

  // Modern event listener with better browser support
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleChange);
  } else {
    // Fallback for older browsers
    mediaQuery.addListener(handleChange);
  }

  // Cleanup function
  return () => {
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener("change", handleChange);
    } else {
      mediaQuery.removeListener(handleChange);
    }
  };
};

// Export theme helpers
export const themeHelpers = {
  getThemeDisplayName: (theme) => {
    const names = {
      [THEME_OPTIONS.LIGHT]: "فاتح",
      [THEME_OPTIONS.DARK]: "داكن",
      [THEME_OPTIONS.SYSTEM]: "النظام",
    };
    return names[theme] || theme;
  },

  getThemeIcon: (theme) => {
    const icons = {
      [THEME_OPTIONS.LIGHT]: "☀️",
      [THEME_OPTIONS.DARK]: "🌙",
      [THEME_OPTIONS.SYSTEM]: "💻",
    };
    return icons[theme] || "⚡";
  },

  getNextTheme: (currentTheme) => {
    const themes = Object.values(THEME_OPTIONS);
    const currentIndex = themes.indexOf(currentTheme);
    return themes[(currentIndex + 1) % themes.length];
  },
};

export const {
  toggleTheme,
  setTheme,
  setLightTheme,
  setDarkTheme,
  setSystemTheme,
  updateSystemTheme,
  initializeTheme,
} = themeSlice.actions;

export default themeSlice.reducer;
